import { convert24TimeTo12 } from 'infrastructure/functions';

type TData = {
  [k: string]: Record<string, unknown>;
};

type IResentWeight = {
  vitalType?: string;
  vitalDate?: string;
  vitalTime?: string;
  vitalValue?: string;
};

const toSentence = <TValue = unknown>(val: TValue) => {
  if (Array.isArray(val)) {
    if (val.every((i) => typeof i === 'string')) return val.join(', ');
    return val.map((i) => ('value' in i ? i.value : i)).join(', ');
  }
  return val;
};

const formatRecentWeights = (arr: IResentWeight[]) => {
  const output = arr.map((i) => {
    const { vitalType, vitalDate, vitalTime, vitalValue } = i;
    let outputText = '';
    const type = vitalType ? `${vitalType} ` : '';
    const date = vitalDate ? `on ${vitalDate}` : '';
    const time = vitalTime ? ` at ${convert24TimeTo12(`${vitalTime}:00`)}` : '';
    const weight = vitalValue ? ` (${vitalValue} pounds)` : '';
    if (type) outputText += type;
    if (date) outputText += date;
    if (time) outputText += time;
    if (weight) outputText += weight;

    return outputText.trim();
  });
  return output.join(', ');
};

const isStepEmpty = (step: Record<string, unknown>) => {
  return Object.values(step).every((i) => !i);
};

export const generateCkdCcmClinicalNotesTemplate = <T extends TData>(
  values: T,
) => {
  const { STEP1, STEP2, STEP3, STEP4, STEP5 } = values;

  if (
    STEP3?.recentWeights &&
    Array.isArray(STEP3.recentWeights) &&
    !STEP3.recentWeights.every((i) => Object.values(i).length)
  ) {
    STEP3.recentWeights = undefined;
  }

  let text = '';

  // --- STEP 1: CKD History ---
  if (!isStepEmpty(STEP1)) {
    text += 'STEP 1: Initial Overview CKD\n';

    if (STEP1?.howLongTreatedCkd) {
      text += `The patient has been treated for CKD for ${toSentence(
        STEP1.howLongTreatedCkd,
      )}. `;
    }
    if (STEP1?.howLongTreatedCkdNote) {
      text += `Note: ${STEP1.howLongTreatedCkdNote}. `;
      text += '\n';
    } else {
      text += '\n';
    }
    if (STEP1?.effectsOnBody) {
      text += `The patient's understanding of how CKD affects the body is ${toSentence(
        STEP1.effectsOnBody,
      )}. `;
    }
    if (STEP1?.effectsOnBodyNote) {
      text += `Note: ${STEP1.effectsOnBodyNote}. `;
      text += '\n';
    } else {
      text += '\n';
    }
    if (STEP1?.hospitalized) {
      text += `Have you been hospitalized for CKD?: ${toSentence(
        STEP1.hospitalized,
      )}. `;
      text += '\n';
    }
    if (STEP1?.outpatientServices) {
      text += `Have you received outpatient services for CKD?: ${toSentence(
        STEP1.outpatientServices,
      )}. `;
      text += '\n';
    }
    if (STEP1?.familyHistory) {
      text += `Do you have a family history of CKD?: ${toSentence(
        STEP1.familyHistory,
      )}. `;
      text += '\n';
    }
    if (STEP1?.pretransplantAssessment) {
      text += `Have you had a pretransplant assessment?: ${toSentence(
        STEP1.pretransplantAssessment,
      )}. `;
    }
    if (STEP1?.pretransplantAssessmentNote) {
      text += `Note: ${STEP1.pretransplantAssessmentNote}. `;
    }
  }

  // --- STEP 2: Symptoms & Education ---
  if (!isStepEmpty(STEP2)) {
    text += '\n\n';
    text += 'STEP 2: Signs & Symptoms\n';
    if (STEP2?.recognizesSignsSymptoms) {
      text += `Patient recognizes these Signs and Symptoms of CKD (Limit 4): ${toSentence(
        STEP2.recognizesSignsSymptoms,
      )}.`;
      text += '\n';
    }
    if (STEP2?.snInstructions) {
      text += `SN instructed Patient on these Signs and Symptoms of CKD (Limit 4): ${toSentence(
        STEP2.snInstructions,
      )}. `;
      text += '\n';
    }
    if (STEP2?.notifyProvider) {
      text += `SN instructed Patient to notify Provider if any of these Signs and Symptoms occur: ${toSentence(
        STEP2.notifyProvider,
      )}. `;
      text += '\n';
    }
    if (STEP2?.symptomsTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP2.symptomsTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP2?.symptomsVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP2.symptomsVerbalizeEducation,
      )}. `;
    }
    if (STEP2?.symptomsVerbalizeEducationNote) {
      text += `Note: ${STEP2.symptomsVerbalizeEducationNote}. `;
    }
  }

  // --- STEP 3: Fluid & Weight Management ---
  if (!isStepEmpty(STEP3)) {
    text += '\n\n';
    text += 'STEP 3: Limiting Fluids and Monitors Weight\n';

    if (STEP3?.limitingFluids) {
      text += `SN educated Patient on the importance of limiting fluids (Limit 4): ${toSentence(
        STEP3.limitingFluids,
      )}. `;
      text += '\n';
    }
    if (STEP3?.helpQuenchThirst) {
      text += `To help quench your thirst, you might try to (Limit 4): ${toSentence(
        STEP3.helpQuenchThirst,
      )}. `;
      text += '\n';
    }
    if (STEP3?.fluidsTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP3.fluidsTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP3?.fluidsVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP3.fluidsVerbalizeEducation,
      )}. `;
    }
    if (STEP3?.fluidsVerbalizeEducationNote) {
      text += `Note: ${STEP3.fluidsVerbalizeEducationNote}. `;
      text += '\n';
    } else {
      text += '\n';
    }
    if (STEP3?.monitorWeight) {
      text += `Do you monitor weight?: ${toSentence(STEP3.monitorWeight)}. `;
      text += '\n';
    }
    if (STEP3?.monitorWeightImportant) {
      text += `SN instructed Patient on why monitoring weight is important: ${toSentence(
        STEP3.monitorWeightImportant,
      )}. `;
      text += '\n';
    }
    if (STEP3?.haveScale) {
      text += `Do you have a scale?: ${toSentence(STEP3.haveScale)}. `;
      text += '\n';
    }
    if (STEP3?.measureWeight) {
      text += `SN instructed Patient on consistent steps to measure weight: ${toSentence(
        STEP3.measureWeight,
      )}. `;
      text += '\n';
    }
    if (
      STEP3?.recentWeights &&
      Array.isArray(STEP3.recentWeights) &&
      STEP3.recentWeights.every((i) => Object.values(i).length)
    ) {
      text += `What are Patient's recent WEIGHTS?: ${formatRecentWeights(
        STEP3.recentWeights,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP3.weightTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP3.weightVerbalizeEducation,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightVerbalizeEducationNote) {
      text += `Note: ${STEP3.weightVerbalizeEducationNote}. `;
    }
    if (STEP3?.reinforcementNote) {
      text += `Explain need for reinforcement: ${STEP3.reinforcementNote}. `;
    }
  }

  // --- STEP 4: Dialysis ---
  if (!isStepEmpty(STEP4)) {
    text += '\n\n';
    text += 'STEP 4: Dialysis Assessment\n';

    if (STEP4?.dialysis) {
      text += `Do you attend dialysis?: ${toSentence(STEP4.dialysis)}. `;
      text += '\n';
    }
    if (STEP4?.dialysisType) {
      text += `What type of dialysis?: ${toSentence(STEP4.dialysisType)}. `;
      text += '\n';
    }
    if (STEP4?.dialysisFrequency) {
      text += `How often do you do dialysis?: ${toSentence(
        STEP4.dialysisFrequency,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisNurse) {
      text += `Has a home dialysis nurse visited recently?: ${toSentence(
        STEP4.dialysisNurse,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisSession) {
      text += `Have you had trouble completing a full dialysis session more than 1x week?: ${toSentence(
        STEP4.dialysisSession,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisAccess) {
      text += `How do you get access for dialysis?: ${toSentence(
        STEP4.dialysisAccess,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisTrained) {
      text += `Patient is trained and able to perform (Limit 4): ${toSentence(
        STEP4.dialysisTrained,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisNotTrained) {
      text += `Patient is NOT trained or able to perform (Limit 4): ${toSentence(
        STEP4.dialysisNotTrained,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP4.dialysisTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP4?.dialysisVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP4.dialysisVerbalizeEducation,
      )}. `;
    }
    if (STEP4?.dialysisVerbalizeEducationNote) {
      text += `Note: ${STEP4.dialysisVerbalizeEducationNote}. `;
    }
  }

  // --- STEP 5: Hemodialysis Access ---
  if (!isStepEmpty(STEP5)) {
    text += '\n\n';
    text += 'STEP 5: Caring for Fistulas and Catheters - Dialysis\n';

    if (STEP5?.hemodialysisFistula) {
      text += `SN instructed Patient on protecting and caring for a hemodialysis fistula or graft (Limit 4): ${toSentence(
        STEP5.hemodialysisFistula,
      )}. `;
      text += '\n';
    }
    if (STEP5?.hemodialysisCatheter) {
      text += `SN instructed Patient on protecting and caring for a hemodialysis catheter (Limit 4): ${toSentence(
        STEP5.hemodialysisCatheter,
      )}. `;
      text += '\n';
    }
    if (STEP5?.hemodialysisFistulaCheck) {
      text += `SN instructed Patient on protecting and caring for a hemodialysis fistula or graft (Limit 4): ${toSentence(
        STEP5.hemodialysisFistulaCheck,
      )}. `;
      text += '\n';
    }
    if (STEP5?.hemodialysisCatheterCheck) {
      text += `SN instructed Patient on protecting and caring for a hemodialysis catheter (Limit 4): ${toSentence(
        STEP5.hemodialysisCatheterCheck,
      )}. `;
      text += '\n';
    }
    if (STEP5?.peritonealCatheter) {
      text += `SN instructed Patient on protecting and caring for a peritoneal catheter (Limit 4): ${toSentence(
        STEP5.peritonealCatheter,
      )}. `;
      text += '\n';
    }
    if (STEP5?.fistulaCatheterTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP5.fistulaCatheterTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP5?.fistulaCatheterVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP5.fistulaCatheterVerbalizeEducation,
      )}. `;
    }
    if (STEP5?.fistulaCatheterVerbalizeEducationNote) {
      text += `Note: ${STEP5.fistulaCatheterVerbalizeEducationNote}. `;
    }
  }
  return text.trim();
};
